import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { LysepAPI } from '../../contexts/ApiContext';
import { toast } from 'react-toastify';
import {
    ArrowLeft,
    RefreshCw,
    Wifi,
    Cpu,
    Network,
    Terminal,
    Shield,
    Settings,
    FileText,
    Key,
    Server as ServerIcon,
    ChevronDown,
    ChevronRight,
    Search,
    Clock,
    Menu,
    Box, // Added for Docker icon
    Users, // Added for Users tab
    Globe // Added for DNS tab
} from 'lucide-react';
import InterfacesTab from './tabs/InterfacesTab';
import SshTab from './tabs/SshTab';
import FirewallTab from './tabs/FirewallTab';
import ResourcesTab from './tabs/ResourcesTab';
import DockerContainers from './tabs/DockerContainers';
import UsersTab from './tabs/UsersTab';
import DNSTab from './tabs/DNSTab';
import DNSManagement from './dns/DNSManagement';

// Sidebar Menu Items
const MENU_ITEMS = [
    { id: 'summary', label: 'Özet', icon: FileText },
    { id: 'resources', label: 'Kaynaklar', icon: Cpu },
    { id: 'interfaces', label: 'Ağ Arayüzleri', icon: Network },
    { id: 'users', label: 'Kullanıcılar', icon: Users },
    {
        id: 'ssh',
        label: 'SSH',
        icon: Terminal,
        children: [
            { id: 'ssh/connection', label: 'Bağlantı', icon: Key },
            { id: 'ssh/sessions', label: 'Oturumlar', icon: Terminal }
        ]
    },
    { id: 'docker', label: 'Docker', icon: Box },
    { id: 'dns', label: 'DNS Yönetimi', icon: Globe },
    { id: 'firewall', label: 'Güvenlik Duvarı', icon: Shield },
    { id: 'settings', label: 'Ayarlar', icon: Settings }
];

const ServerDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [server, setServer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [testingConnection, setTestingConnection] = useState(false);
    const [expandedMenus, setExpandedMenus] = useState(['ssh']); // Default expand SSH

    // Get active tab from URL path
    const getActiveTabFromUrl = () => {
        const parts = location.pathname.split('/').filter(Boolean);
        const idIndex = parts.indexOf(id);
        if (idIndex === -1 || idIndex === parts.length - 1) return 'summary';
        return parts.slice(idIndex + 1).join('/');
    };

    const [activeTab, setActiveTab] = useState(getActiveTabFromUrl());

    // Update URL when tab changes
    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        navigate(`/panel/servers/${id}/${newTab}`);
    };

    // Toggle menu expansion
    const toggleMenu = (menuId) => {
        setExpandedMenus(prev =>
            prev.includes(menuId)
                ? prev.filter(id => id !== menuId)
                : [...prev, menuId]
        );
    };

    // Sync tab with URL path changes
    useEffect(() => {
        setActiveTab(getActiveTabFromUrl());
    }, [location.pathname]);

    // Redirect to /summary if at base ID path
    useEffect(() => {
        const parts = location.pathname.split('/').filter(Boolean);
        // Check if the path ends with the ID
        if (parts[parts.length - 1] === id) {
            navigate(`/panel/servers/${id}/summary`, { replace: true });
        }
    }, [location.pathname, id, navigate]);

    const fetchServer = async () => {
        setLoading(true);
        try {
            const resp = await LysepAPI.get(`/api/servers/${id}`);
            if (resp.data.state) {
                setServer(resp.data.data);
            } else {
                toast.error(resp.data.message);
                navigate('/panel/servers');
            }
        } catch (error) {
            console.error(error);
            toast.error("Sunucu bilgileri alınamadı");
            navigate('/panel/servers');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServer();
    }, [id]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <RefreshCw className="w-8 h-8 animate-spin mb-3 text-blue-500" />
                <p>Yükleniyor...</p>
            </div>
        );
    }

    if (!server) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <ServerIcon className="w-12 h-12 mb-3 opacity-20" />
                <p>Sunucu bulunamadı.</p>
                <button onClick={() => navigate('/panel/servers')} className="mt-4 text-blue-400 hover:text-blue-300">
                    Listeye Dön
                </button>
            </div>
        );
    }

    const renderSummary = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 animate-fadeIn pb-6">
            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-2 mb-4 text-gray-400 border-b border-white/5 pb-2">
                    <ServerIcon className="w-5 h-5" />
                    <h3 className="text-sm font-semibold uppercase tracking-wider">Temel Bilgiler</h3>
                </div>

                <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                        <span className="text-sm text-gray-500">IP Adresi</span>
                        <span className="col-span-2 text-sm text-white font-mono">{server.ip_address}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <span className="text-sm text-gray-500">Durum</span>
                        <div className="col-span-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${server.status === 'active'
                                ? 'bg-green-500/10 border-green-500/20 text-green-400'
                                : 'bg-gray-500/10 border-gray-500/20 text-gray-400'
                                }`}>
                                {server.status === 'active' ? 'Aktif' : server.status || 'Bilinmiyor'}
                            </span>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <span className="text-sm text-gray-500">Son Bağlantı</span>
                        <span className="col-span-2 text-sm text-white flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-gray-500" />
                            {server.last_connection_at
                                ? new Date(server.last_connection_at).toLocaleString()
                                : 'Hiç bağlanılmadı'}
                        </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <span className="text-sm text-gray-500">Açıklama</span>
                        <span className="col-span-2 text-sm text-gray-300">{server.description || '-'}</span>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderSshConnection = () => (
        <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-6 shadow-lg animate-fadeIn max-w-2xl pb-6">
            <div className="flex items-center gap-2 mb-4 text-gray-400 border-b border-white/5 pb-2">
                <Key className="w-5 h-5" />
                <h3 className="text-sm font-semibold uppercase tracking-wider">Bağlantı & Kimlik</h3>
            </div>

            <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 items-center">
                    <span className="text-sm text-gray-500">Bağlı Kimlik</span>
                    <div className="col-span-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm">
                            <Key className="w-3.5 h-3.5" />
                            {server.credential_name || 'Yok'}
                        </span>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <span className="text-sm text-gray-500">Bağlantı Dizisi</span>
                    <div className="col-span-2">
                        <code className="bg-black/30 px-3 py-1.5 rounded-lg text-sm text-green-400 font-mono block w-full">
                            ssh {server.username ? `${server.username}@` : ''}{server.ip_address}
                        </code>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderSettings = () => (
        <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-6 shadow-lg animate-fadeIn pb-6">
            <h3 className="text-lg font-semibold text-white mb-2">Ayarlar</h3>
            <p className="text-gray-400 text-sm">Sunucu yapılandırma ayarları buraya gelecek.</p>
        </div>
    );

    return (
        <div className="flex flex-col h-full w-full bg-[#0a0e13]">
            {/* Header - Fixed Height */}
            <div className="flex-none p-4 sm:p-6 border-b border-white/10 bg-[#0a0e13]">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/panel/servers')}
                            className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-lg sm:text-2xl font-bold text-white flex items-center gap-2">
                                {server.name}
                            </h1>
                            <p className="text-xs text-gray-500 font-mono mt-0.5">{server.ip_address}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3">
                        <button
                            onClick={fetchServer}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-white bg-[#1e1e1e] hover:bg-[#252525] border border-white/10 rounded-lg transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                            <span className="hidden sm:inline">Yenile</span>
                        </button>
                        <button
                            disabled={testingConnection}
                            onClick={async () => {
                                setTestingConnection(true);
                                const toastId = toast.loading(`Bağlantı test ediliyor: ${server.name}...`);
                                try {
                                    const resp = await LysepAPI.post(`/api/servers/${server.id}/ssh/test`);
                                    if (resp.data.state) {
                                        toast.update(toastId, { render: `Başarılı: ${resp.data.message}`, type: "success", isLoading: false, autoClose: 3000 });
                                        fetchServer();
                                    } else {
                                        toast.update(toastId, { render: `Hata: ${resp.data.message}`, type: "error", isLoading: false, autoClose: 4000 });
                                    }
                                } catch (error) {
                                    toast.update(toastId, { render: `Hata: ${error.response?.data?.message || error.message}`, type: "error", isLoading: false, autoClose: 4000 });
                                } finally {
                                    setTestingConnection(false);
                                }
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium shadow-lg shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {testingConnection ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
                            <span className="hidden sm:inline">Test Et</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Tabs - Visible only on mobile */}
            <div className="lg:hidden flex-none bg-[#0a0e13] border-b border-white/10 sticky top-0 z-20 overflow-x-auto custom-scrollbar">
                <div className="flex items-center px-4 py-3 gap-2 min-w-max">
                    {(() => {
                        // Flatten menu items for mobile
                        const flattenedItems = [];
                        MENU_ITEMS.forEach(item => {
                            if (item.children) {
                                item.children.forEach(child => flattenedItems.push({ ...child, parentLabel: item.label }));
                            } else {
                                flattenedItems.push(item);
                            }
                        });

                        return flattenedItems.map(item => {
                            const Icon = item.icon;
                            // Check exact match for activeTab or startWith for parent items (though we flattened them)
                            const isActive = activeTab === item.id;

                            return (
                                <button
                                    key={item.id}
                                    onClick={() => handleTabChange(item.id)}
                                    className={`
                                        flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all
                                        ${isActive
                                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25 ring-1 ring-blue-500/50'
                                            : 'bg-[#1a1a1a] text-gray-400 border border-white/5 hover:bg-[#252525] hover:text-white'
                                        }
                                    `}
                                >
                                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                                    <span>{item.label}</span>
                                </button>
                            );
                        });
                    })()}
                </div>
            </div>

            {/* Content Area - Filling Remaining Space */}
            <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
                {/* Navigation: Vertical Sidebar on Desktop (Hidden on Mobile) */}
                <div className="hidden lg:block lg:w-64 lg:h-full lg:overflow-y-auto lg:border-r border-white/10 bg-[#0a0e13] custom-scrollbar z-10">
                    <div className="p-4 w-full">
                        <nav className="flex flex-col gap-2">
                            {MENU_ITEMS.map((item) => {
                                const Icon = item.icon;
                                const isActive = activeTab === item.id || (item.children && activeTab.startsWith(item.id));
                                const hasChildren = item.children && item.children.length > 0;
                                const isExpanded = expandedMenus.includes(item.id);

                                return (
                                    <div key={item.id} className="flex flex-col">
                                        <button
                                            onClick={() => {
                                                if (hasChildren) {
                                                    // On mobile we might want to just activate/expand logic differently
                                                    // But for simplicity, toggle expand + if desktop
                                                    toggleMenu(item.id);
                                                    if (!expandedMenus.includes(item.id)) {
                                                        // optional: auto-select first child?
                                                    }
                                                } else {
                                                    handleTabChange(item.id);
                                                }
                                            }}
                                            className={`
                                                relative flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm font-medium whitespace-nowrap
                                                ${isActive && !hasChildren
                                                    ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                                }
                                            `}
                                        >
                                            <Icon className={`w-4 h-4 ${isActive ? 'text-blue-400' : 'text-gray-500'}`} />
                                            <span>{item.label}</span>
                                            {hasChildren && (
                                                <div className="hidden lg:block ml-auto">
                                                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                                </div>
                                            )}
                                        </button>

                                        {/* Nested Menu: Only visible when expanded. On mobile, it appears below or inline? 
                                            For mobile horizontal scroll, nested items are tricky. 
                                            Let's just show them inline if expanded for now, or just flatten the list visually on mobile?
                                        */}
                                        {/* Nested Menu: Only visible when expanded. Desktop only. */}
                                        {hasChildren && isExpanded && (
                                            <div className="hidden lg:flex flex-col lg:ml-4 lg:pl-3 lg:border-l border-white/10 space-y-1 mt-1">
                                                {item.children.map((child) => {
                                                    const ChildIcon = child.icon;
                                                    const isChildActive = activeTab === child.id;
                                                    return (
                                                        <button
                                                            key={child.id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleTabChange(child.id);
                                                            }}
                                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm whitespace-nowrap ${isChildActive
                                                                ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                                                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                                                }`}
                                                        >
                                                            <ChildIcon className={`w-3.5 h-3.5 ${isChildActive ? 'text-blue-400' : 'text-gray-500'}`} />
                                                            <span>{child.label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Mobile-only children rendering logic */}
                                        {hasChildren && (
                                            item.children.map((child) => {
                                                const ChildIcon = child.icon;
                                                const isChildActive = activeTab === child.id;
                                                return (
                                                    <button
                                                        key={`mobile-${child.id}`}
                                                        onClick={() => handleTabChange(child.id)}
                                                        className={`lg:hidden flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm whitespace-nowrap ml-2 ${isChildActive
                                                            ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                                                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                                                            }`}
                                                    >
                                                        <ChildIcon className={`w-3.5 h-3.5 ${isChildActive ? 'text-blue-400' : 'text-gray-500'}`} />
                                                        <span>{child.label}</span>
                                                    </button>
                                                )
                                            })
                                        )}
                                    </div>
                                );
                            })}
                        </nav>
                    </div>
                </div>

                {/* Tab Content Container */}
                <div className="flex-1 h-full overflow-y-auto overflow-x-hidden p-4 sm:p-6 custom-scrollbar">
                    <div className="max-w-7xl mx-auto h-full">
                        {/* max-w-7xl ensures content doesn't stretch too wide on huge screens, but h-full ensures it fills height */}
                        {activeTab === 'summary' && renderSummary()}
                        {activeTab === 'interfaces' && <InterfacesTab serverId={id} />}
                        {activeTab === 'resources' && <ResourcesTab serverId={id} />}
                        {activeTab === 'users' && <UsersTab serverId={id} />}
                        {activeTab === 'ssh/connection' && renderSshConnection()}
                        {activeTab === 'ssh/sessions' && <SshTab serverId={id} />}
                        {activeTab === 'docker' && <DockerContainers serverId={id} />}
                        {activeTab === 'dns' && <DNSTab serverId={id} />}
                        {activeTab.startsWith('dns/management') && <DNSManagement />}
                        {activeTab === 'firewall' && <FirewallTab serverId={id} />}
                        {activeTab === 'settings' && renderSettings()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServerDetail;
